/**
 * Single Cycle Detail View - Command Center
 * 
 * This is the operational hub where users:
 * 1. See flywheel progress (which stage they're in)
 * 2. Understand coverage (which requirements are done, which have gaps)
 * 3. Take action (approve claims, assign tasks, upload evidence)
 * 4. Track accountability (who owns what, who's approved what)
 * 5. Access audit defensibility (view trail, generate packs)
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar, 
  FileText, 
  CheckCircle2, 
  AlertTriangle,
  Clock,
  Upload,
  Shield,
  FileBarChart,
  GitMerge,
  Users,
  ChevronRight,
  Download,
  Plus,
  Settings,
  BarChart3,
  Eye,
  X,
  RefreshCw
} from 'lucide-react';
import { API } from '../../App';
import { apiFetch } from '../../services/api_service';
import DefensibilityFlywheel from '../../components/DefensibilityFlywheel';

// ============================================================================
// FLYWHEEL STAGES CONFIG
// ============================================================================
const FLYWHEEL_STAGES = [
  { key: 'upload', label: 'Upload', icon: Upload, color: 'emerald' },
  { key: 'mapping', label: 'Mapping', icon: GitMerge, color: 'blue' },
  { key: 'evidence_review', label: 'Evidence Review', icon: FileText, color: 'purple' },
  { key: 'audit_trail', label: 'Audit Trail', icon: Shield, color: 'amber' },
  { key: 'report', label: 'Report', icon: FileBarChart, color: 'cyan' },
];

const STATUS_CONFIG = {
  draft: { label: 'Draft', bg: 'bg-[#1F1F1F]', text: 'text-[#A0A0A0]' },
  in_progress: { label: 'In Progress', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  in_review: { label: 'In Review', bg: 'bg-purple-500/20', text: 'text-purple-400' },
  approved: { label: 'Approved', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  published: { label: 'Published', bg: 'bg-cyan-500/20', text: 'text-cyan-400' },
};

// ============================================================================
// FLYWHEEL STAGE INDICATOR COMPONENT
// ============================================================================
const FlywheelStageIndicator = ({ currentStage, stageProgress, variant = 'compact' }) => {
  const currentStageIndex = FLYWHEEL_STAGES.findIndex(s => s.key === currentStage);
  
  if (variant === 'compact') {
    return (
      <div className="flex items-center gap-2 mt-4">
        {FLYWHEEL_STAGES.map((stage, idx) => {
          const Icon = stage.icon;
          const progress = stageProgress?.[stage.key]?.completion_percentage || 0;
          const isCompleted = idx < currentStageIndex || (idx === currentStageIndex && progress === 100);
          const isCurrent = idx === currentStageIndex;
          
          return (
            <React.Fragment key={stage.key}>
              <div className="flex items-center gap-2">
                <div className={`p-1.5 rounded-lg ${
                  isCompleted ? `bg-${stage.color}-500/30` :
                  isCurrent ? `bg-${stage.color}-500/20 ring-2 ring-${stage.color}-500/50` :
                  'bg-[#1F1F1F]'
                }`}>
                  <Icon size={14} className={
                    isCompleted ? `text-${stage.color}-400` :
                    isCurrent ? `text-${stage.color}-400` :
                    'text-[#6B6B6B]'
                  } />
                </div>
                <span className={`text-xs ${
                  isCurrent ? 'text-white font-medium' : 'text-[#6B6B6B]'
                }`}>
                  {stage.label}
                </span>
                {isCurrent && progress > 0 && progress < 100 && (
                  <span className="text-xs text-[#A0A0A0]">{progress}%</span>
                )}
              </div>
              {idx < FLYWHEEL_STAGES.length - 1 && (
                <ChevronRight size={12} className={
                  idx < currentStageIndex ? 'text-emerald-400' : 'text-[#2F2F2F]'
                } />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }
  
  // Full variant for sidebar
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
      <h3 className="text-sm font-semibold text-white mb-4">Flywheel Progress</h3>
      <div className="space-y-3">
        {FLYWHEEL_STAGES.map((stage, idx) => {
          const Icon = stage.icon;
          const progress = stageProgress?.[stage.key]?.completion_percentage || 0;
          const isCompleted = idx < currentStageIndex || (idx === currentStageIndex && progress === 100);
          const isCurrent = idx === currentStageIndex;
          
          return (
            <div key={stage.key} className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                isCompleted ? 'bg-emerald-500/20' :
                isCurrent ? `bg-${stage.color}-500/20` :
                'bg-[#1F1F1F]'
              }`}>
                {isCompleted ? (
                  <CheckCircle2 size={16} className="text-emerald-400" />
                ) : (
                  <Icon size={16} className={isCurrent ? `text-${stage.color}-400` : 'text-[#6B6B6B]'} />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className={`text-sm ${isCurrent ? 'text-white font-medium' : 'text-[#A0A0A0]'}`}>
                    {String(idx + 1).padStart(2, '0')} {stage.label}
                  </span>
                  <span className={`text-xs ${
                    isCompleted ? 'text-emerald-400' :
                    isCurrent ? `text-${stage.color}-400` :
                    'text-[#6B6B6B]'
                  }`}>
                    {isCompleted ? 'Complete' : isCurrent ? `${progress}%` : 'Not Started'}
                  </span>
                </div>
                {isCurrent && (
                  <div className="mt-1 h-1 bg-[#1F1F1F] rounded-full overflow-hidden">
                    <div 
                      className={`h-full bg-${stage.color}-500 rounded-full transition-all`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// REQUIREMENTS COVERAGE CARD
// ============================================================================
const RequirementsCoverageCard = ({ cycle }) => {
  const coverage = cycle.requirements_coverage || { covered: 0, total: 100, coverage_percentage: 0 };
  
  // Mock topic breakdown - in production this would come from backend
  const topicBreakdown = [
    { code: 'E1', name: 'Climate Change', covered: 142, total: 167, percentage: 85 },
    { code: 'S1', name: 'Own Workforce', covered: 81, total: 156, percentage: 52 },
    { code: 'G1', name: 'Business Conduct', covered: 58, total: 120, percentage: 48 },
  ];

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white">Requirements Coverage</h3>
        <span className={`px-2 py-1 text-xs rounded ${
          coverage.coverage_percentage >= 80 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
        }`}>
          {coverage.covered || 0} / {coverage.total || 0}
        </span>
      </div>
      
      {/* Overall Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-[#A0A0A0]">Overall Coverage</span>
          <span className="text-xl font-bold text-white">{coverage.coverage_percentage || 0}%</span>
        </div>
        <div className="h-2.5 bg-[#1F1F1F] rounded-full overflow-hidden">
          <div 
            className="h-full bg-emerald-500 rounded-full transition-all"
            style={{ width: `${coverage.coverage_percentage || 0}%` }}
          />
        </div>
      </div>

      <div className="border-t border-[#1F1F1F] pt-4">
        <p className="text-xs text-[#A0A0A0] mb-3">Breakdown by Topic:</p>
        <div className="space-y-3">
          {topicBreakdown.map(topic => (
            <div key={topic.code} className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-white font-medium">
                  {topic.code} {topic.name}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-[#6B6B6B]">{topic.covered}/{topic.total}</span>
                  {topic.percentage >= 80 ? (
                    <CheckCircle2 size={14} className="text-emerald-400" />
                  ) : (
                    <AlertTriangle size={14} className="text-amber-400" />
                  )}
                </div>
              </div>
              <div className="h-1 bg-[#1F1F1F] rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full ${topic.percentage >= 80 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                  style={{ width: `${topic.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CLAIMS STATUS CARD
// ============================================================================
const ClaimsStatusCard = ({ claims, cycleId }) => {
  const statusCounts = {
    approved: claims?.filter(c => c.ownership?.approval_status === 'approved').length || 0,
    pending: claims?.filter(c => c.ownership?.approval_status === 'pending').length || 0,
    draft: claims?.filter(c => c.ownership?.approval_status === 'draft').length || 0,
  };
  const total = (claims?.length || 0);

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-white">Claims Status</h3>
        <Link 
          to={`/cycles/${cycleId}/claims`}
          className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
        >
          View All <ChevronRight size={12} />
        </Link>
      </div>

      <div className="text-2xl font-bold text-white mb-4">{total} claims created</div>

      <div className="space-y-2">
        <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} className="text-emerald-400" />
            <span className="text-sm text-white">Approved</span>
          </div>
          <span className="text-sm font-semibold text-emerald-400">{statusCounts.approved}</span>
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-amber-400" />
            <span className="text-sm text-white">Pending Approval</span>
          </div>
          <span className="text-sm font-semibold text-amber-400">{statusCounts.pending}</span>
        </div>

        <div className="flex items-center justify-between p-3 rounded-lg bg-[#1F1F1F]">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-[#A0A0A0]" />
            <span className="text-sm text-white">Draft</span>
          </div>
          <span className="text-sm font-semibold text-[#A0A0A0]">{statusCounts.draft}</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// GAPS & RISKS CARD
// ============================================================================
const GapsRisksCard = ({ gaps }) => {
  if (!gaps || gaps.length === 0) return null;

  return (
    <div className="bg-[#111111] border border-amber-500/30 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle size={16} className="text-amber-400" />
        <h3 className="text-sm font-semibold text-amber-400">Gaps & Risks ({gaps.length})</h3>
      </div>

      <div className="space-y-3">
        {gaps.slice(0, 3).map((gap, idx) => (
          <div key={idx} className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
            <div className="flex items-start justify-between gap-3 mb-2">
              <p className="text-sm text-white flex-1">{gap.obligation_title || gap.description}</p>
              <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded ${
                gap.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                gap.severity === 'high' ? 'bg-amber-500/20 text-amber-400' :
                'bg-[#1F1F1F] text-[#A0A0A0]'
              }`}>
                {gap.severity || gap.gap_type}
              </span>
            </div>
            <p className="text-xs text-[#6B6B6B] mb-2">
              {gap.framework_code} {gap.requirement_code}
            </p>
            <button className="w-full px-3 py-2 text-xs font-medium text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 rounded-lg transition-colors">
              Assign Task →
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// TIMELINE MILESTONES CARD
// ============================================================================
const TimelineMilestonesCard = ({ cycle }) => {
  const deadline = cycle.filing_deadline ? new Date(cycle.filing_deadline) : new Date();
  const daysRemaining = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24));

  const milestones = cycle.milestones || [
    { name: 'Data collection', date: '2025-01-31', status: 'completed' },
    { name: 'Internal review', date: '2025-02-28', status: 'in_progress' },
    { name: 'External audit', date: '2025-03-20', status: 'pending' },
  ];

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
      <h3 className="text-sm font-semibold text-white mb-4">Timeline & Milestones</h3>
      
      <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 mb-4">
        <p className="text-xs text-blue-400 font-medium mb-1">Filing Deadline</p>
        <p className="text-sm font-semibold text-white">
          {deadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </p>
        <p className={`text-xs mt-1 ${daysRemaining < 30 ? 'text-amber-400' : 'text-blue-400'}`}>
          {daysRemaining} days remaining
        </p>
      </div>

      <div className="space-y-2">
        {milestones.map((milestone, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm">
            {milestone.status === 'completed' ? (
              <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />
            ) : milestone.status === 'in_progress' ? (
              <div className="w-3.5 h-3.5 rounded-full border-2 border-blue-500 shrink-0" />
            ) : (
              <div className="w-3.5 h-3.5 rounded-full border-2 border-[#2F2F2F] shrink-0" />
            )}
            <span className="flex-1 text-[#A0A0A0]">{milestone.name}</span>
            <span className="text-xs text-[#6B6B6B]">
              {new Date(milestone.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// TEAM & CONTRIBUTORS CARD
// ============================================================================
const TeamContributorsCard = ({ cycle }) => {
  const owner = cycle.ownership?.cycle_owner || { user_name: 'Not assigned' };
  const contributors = cycle.ownership?.contributors || [];

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
      <h3 className="text-sm font-semibold text-white mb-4">Team & Contributors</h3>
      
      {/* Owner */}
      <div className="mb-4">
        <p className="text-xs text-[#6B6B6B] mb-2">Owner</p>
        <div className="flex items-center gap-3 p-3 rounded-lg bg-[#0A0A0A]">
          <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
            <span className="text-xs font-semibold text-emerald-400">
              {owner.user_name?.split(' ').map(n => n[0]).join('') || '?'}
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-white">{owner.user_name}</p>
            <p className="text-xs text-[#6B6B6B]">{owner.user_role || 'Cycle Owner'}</p>
          </div>
        </div>
      </div>

      {contributors.length > 0 && (
        <>
          <div className="border-t border-[#1F1F1F] pt-4 mb-4">
            <p className="text-xs text-[#6B6B6B] mb-2">Contributors</p>
            <div className="space-y-2">
              {contributors.slice(0, 3).map((contributor, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <div className="w-6 h-6 rounded-full bg-[#1F1F1F] flex items-center justify-center">
                    <span className="text-[10px] font-medium text-[#A0A0A0]">
                      {contributor.user_name?.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <span className="text-[#A0A0A0]">{contributor.user_name}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Approval Chain - Mock */}
      <div className="border-t border-[#1F1F1F] pt-4">
        <p className="text-xs text-[#6B6B6B] mb-2">Approval Chain</p>
        <div className="space-y-2">
          {[
            { step: 1, role: 'Sustainability Manager', status: 'completed' },
            { step: 2, role: 'ESG Head', status: 'in_progress' },
            { step: 3, role: 'CFO', status: 'pending' },
          ].map(step => (
            <div key={step.step} className="flex items-center gap-2 text-sm">
              {step.status === 'completed' ? (
                <CheckCircle2 size={14} className="text-emerald-400" />
              ) : step.status === 'in_progress' ? (
                <div className="w-3.5 h-3.5 rounded-full border-2 border-blue-500" />
              ) : (
                <div className="w-3.5 h-3.5 rounded-full border-2 border-[#2F2F2F]" />
              )}
              <span className="text-[#A0A0A0]">Step {step.step}: {step.role}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// QUICK ACTIONS CARD
// ============================================================================
const QuickActionsCard = ({ cycleId, onGenerateAuditPack, isGenerating }) => {
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
      <h3 className="text-sm font-semibold text-white mb-4">Quick Actions</h3>
      <div className="space-y-2">
        <Link 
          to={`/cycles/${cycleId}/claims/new`}
          className="flex items-center gap-3 w-full p-3 text-sm text-[#A0A0A0] hover:text-white bg-[#0A0A0A] hover:bg-[#1F1F1F] rounded-lg transition-colors"
        >
          <Plus size={16} />
          <span>Add Claim</span>
        </Link>
        <Link 
          to={`/evidence/upload?cycle=${cycleId}`}
          className="flex items-center gap-3 w-full p-3 text-sm text-[#A0A0A0] hover:text-white bg-[#0A0A0A] hover:bg-[#1F1F1F] rounded-lg transition-colors"
        >
          <Upload size={16} />
          <span>Upload Evidence</span>
        </Link>
        <button 
          onClick={onGenerateAuditPack}
          disabled={isGenerating}
          className={`flex items-center gap-3 w-full p-3 text-sm rounded-lg transition-colors ${
            isGenerating 
              ? 'bg-emerald-500/10 text-emerald-400 cursor-wait' 
              : 'text-[#A0A0A0] hover:text-white bg-[#0A0A0A] hover:bg-[#1F1F1F]'
          }`}
          data-testid="generate-audit-pack-btn"
        >
          {isGenerating ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <Download size={16} />
              <span>Generate Audit Pack</span>
            </>
          )}
        </button>
        <button className="flex items-center gap-3 w-full p-3 text-sm text-[#A0A0A0] hover:text-white bg-[#0A0A0A] hover:bg-[#1F1F1F] rounded-lg transition-colors">
          <FileBarChart size={16} />
          <span>Export Report</span>
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// TAB CONTENT COMPONENTS
// ============================================================================
const ClaimsTabContent = ({ claims, cycleId }) => {
  if (!claims || claims.length === 0) {
    return (
      <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
        <FileText size={48} className="mx-auto text-[#2F2F2F] mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">No claims yet</h3>
        <p className="text-sm text-[#6B6B6B] mb-4">Create your first claim backed by evidence</p>
        <Link 
          to={`/cycles/${cycleId}/claims/new`}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
        >
          <Plus size={16} />
          Create Claim
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white">{claims.length} Claims</h3>
        <Link 
          to={`/cycles/${cycleId}/claims/new`}
          className="flex items-center gap-2 px-3 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors text-sm"
        >
          <Plus size={14} />
          New Claim
        </Link>
      </div>

      <div className="space-y-3">
        {claims.map(claim => (
          <div key={claim.id} className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4 hover:border-emerald-500/30 transition-colors">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#1F1F1F] text-[#A0A0A0] rounded">
                    {claim.framework_code}
                  </span>
                  <span className="text-xs text-[#6B6B6B]">{claim.requirement_code}</span>
                </div>
                <p className="text-sm text-white">{claim.claim_text || 'No claim text'}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded ${
                claim.ownership?.approval_status === 'approved' ? 'bg-emerald-500/20 text-emerald-400' :
                claim.ownership?.approval_status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                'bg-[#1F1F1F] text-[#A0A0A0]'
              }`}>
                {claim.ownership?.approval_status || 'Draft'}
              </span>
            </div>
            {claim.claim_value && (
              <div className="text-lg font-bold text-emerald-400">
                {claim.claim_value.numeric} {claim.claim_value.unit}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const EvidenceTabContent = ({ cycleId }) => {
  return (
    <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
      <Upload size={48} className="mx-auto text-[#2F2F2F] mb-4" />
      <h3 className="text-lg font-medium text-white mb-2">Evidence for this cycle</h3>
      <p className="text-sm text-[#6B6B6B] mb-4">View and manage evidence linked to this disclosure cycle</p>
      <Link 
        to={`/evidence/library?cycle=${cycleId}`}
        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
      >
        <Eye size={16} />
        View Evidence Library
      </Link>
    </div>
  );
};

const AuditTrailTabContent = ({ cycleId }) => {
  return (
    <div className="text-center py-12 bg-[#111111] border border-[#1F1F1F] rounded-xl">
      <Shield size={48} className="mx-auto text-[#2F2F2F] mb-4" />
      <h3 className="text-lg font-medium text-white mb-2">Audit Trail</h3>
      <p className="text-sm text-[#6B6B6B] mb-4">View complete audit trail for this disclosure cycle</p>
      <Link 
        to={`/audit/trail?cycle=${cycleId}`}
        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
      >
        <Eye size={16} />
        View Audit Trail
      </Link>
    </div>
  );
};

const SettingsTabContent = ({ cycle }) => {
  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
      <h3 className="text-lg font-semibold text-white mb-4">Cycle Settings</h3>
      <div className="space-y-4">
        <div>
          <label className="text-xs text-[#6B6B6B] mb-1 block">Cycle Name</label>
          <input 
            type="text"
            defaultValue={cycle.cycle_name}
            className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-emerald-500/50 focus:outline-none"
          />
        </div>
        <div>
          <label className="text-xs text-[#6B6B6B] mb-1 block">Filing Deadline</label>
          <input 
            type="date"
            defaultValue={cycle.filing_deadline?.split('T')[0]}
            className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-emerald-500/50 focus:outline-none"
          />
        </div>
        <div>
          <label className="text-xs text-[#6B6B6B] mb-1 block">Framework</label>
          <input 
            type="text"
            defaultValue={cycle.framework_code}
            disabled
            className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-[#6B6B6B]"
          />
        </div>
        <button className="w-full px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors text-sm font-medium">
          Save Changes
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================
export default function CycleDetailPage() {
  const { cycleId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [cycle, setCycle] = useState(null);
  const [claims, setClaims] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isGeneratingAuditPack, setIsGeneratingAuditPack] = useState(false);
  const [auditPackModal, setAuditPackModal] = useState({ open: false, data: null });

  // Generate Audit Pack handler
  const handleGenerateAuditPack = async () => {
    setIsGeneratingAuditPack(true);
    try {
      const auditPack = await apiFetch(
        `/v1/audit/generate-pack/${cycleId}`,
        { method: 'POST' }
      );

      if (auditPack?._error) {
        throw new Error(auditPack.message || 'Failed to generate audit pack');
      }

      setAuditPackModal({ open: true, data: auditPack });
    } catch (err) {
      console.error('Error generating audit pack:', err);
      alert('Failed to generate audit pack. Please try again.');
    } finally {
      setIsGeneratingAuditPack(false);
    }
  };

  // Download audit pack as JSON
  const downloadAuditPack = () => {
    if (!auditPackModal.data) return;
    
    const dataStr = JSON.stringify(auditPackModal.data, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${auditPackModal.data.pack_id}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Fetch cycle details
  useEffect(() => {
    const fetchCycleData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch cycle details
        const cycleData = await apiFetch(`/disclosure-cycles/${cycleId}`);
        if (cycleData?._error) throw new Error(cycleData.message || 'Failed to fetch cycle');
        setCycle(cycleData);

        // Fetch claims for this cycle
        try {
          const claimsData = await apiFetch(`/claims/by-cycle/${cycleId}`);
          if (!claimsData?._error) {
            setClaims(claimsData.items || []);
          }
        } catch (e) {
          console.log('No claims found for cycle');
        }

        // Fetch gaps
        try {
          const gapsData = await apiFetch(`/obligations/gaps?disclosure_cycle_id=${cycleId}`);
          if (!gapsData?._error) {
            setGaps(gapsData.gaps || []);
          }
        } catch (e) {
          console.log('No gaps found');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (cycleId) {
      fetchCycleData();
    }
  }, [cycleId]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="cycle-detail-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-[#1F1F1F] rounded w-1/3"></div>
          <div className="h-48 bg-[#1F1F1F] rounded"></div>
          <div className="h-64 bg-[#1F1F1F] rounded"></div>
        </div>
      </div>
    );
  }

  if (error || !cycle) {
    return (
      <div className="space-y-6" data-testid="cycle-detail-error">
        <div className="bg-[#111111] border border-red-500/30 rounded-xl p-8 text-center">
          <AlertTriangle size={48} className="mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">
            {error || 'Disclosure cycle not found'}
          </h2>
          <p className="text-sm text-[#6B6B6B] mb-4">
            The cycle you're looking for doesn't exist or couldn't be loaded.
          </p>
          <Link 
            to="/cycles/active"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-white rounded-lg transition-colors"
          >
            <ArrowLeft size={16} />
            Back to Cycles
          </Link>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[cycle.status] || STATUS_CONFIG.draft;
  const deadline = cycle.filing_deadline ? new Date(cycle.filing_deadline) : new Date();
  const daysRemaining = Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24));

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'claims', label: 'Claims', count: claims.length },
    { id: 'evidence', label: 'Evidence' },
    { id: 'audit', label: 'Audit Trail' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="space-y-6" data-testid="cycle-detail-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <Link to="/cycles/active" className="text-[#6B6B6B] hover:text-emerald-400 flex items-center gap-1">
          <ArrowLeft size={14} />
          Back to Cycles
        </Link>
      </div>

      {/* Header Section */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h1 className="text-2xl font-bold text-white">{cycle.cycle_name}</h1>
              <span className={`px-2 py-1 text-xs font-medium rounded ${statusConfig.bg} ${statusConfig.text}`}>
                {statusConfig.label}
              </span>
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#1F1F1F] text-[#A0A0A0] rounded">
                {cycle.framework_code}
              </span>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-[#A0A0A0]">
              <span className="flex items-center gap-1.5">
                <Calendar size={14} />
                Due: {deadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                  daysRemaining < 30 ? 'bg-amber-500/20 text-amber-400' : 'bg-[#1F1F1F] text-[#A0A0A0]'
                }`}>
                  {daysRemaining} days
                </span>
              </span>
              
              <span className="flex items-center gap-1.5">
                <Users size={14} />
                {cycle.ownership?.cycle_owner?.user_name || 'Not assigned'}
              </span>
            </div>
          </div>
        </div>

        {/* Flywheel Indicator - Compact */}
        <FlywheelStageIndicator 
          currentStage={cycle.current_stage || 'upload'} 
          stageProgress={cycle.stage_progress}
          variant="compact"
        />
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center gap-1 p-1 bg-[#111111] border border-[#1F1F1F] rounded-xl">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTab === tab.id 
                ? 'bg-emerald-500/20 text-emerald-400' 
                : 'text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F]'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                activeTab === tab.id ? 'bg-emerald-500/30' : 'bg-[#1F1F1F]'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <RequirementsCoverageCard cycle={cycle} />
            <ClaimsStatusCard claims={claims} cycleId={cycleId} />
            <GapsRisksCard gaps={gaps} />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            <FlywheelStageIndicator 
              currentStage={cycle.current_stage || 'upload'} 
              stageProgress={cycle.stage_progress}
              variant="full"
            />
            <TimelineMilestonesCard cycle={cycle} />
            <TeamContributorsCard cycle={cycle} />
            <QuickActionsCard 
              cycleId={cycleId} 
              onGenerateAuditPack={handleGenerateAuditPack}
              isGenerating={isGeneratingAuditPack}
            />
          </div>
        </div>
      )}

      {activeTab === 'claims' && (
        <ClaimsTabContent claims={claims} cycleId={cycleId} />
      )}

      {activeTab === 'evidence' && (
        <EvidenceTabContent cycleId={cycleId} />
      )}

      {activeTab === 'audit' && (
        <AuditTrailTabContent cycleId={cycleId} />
      )}

      {activeTab === 'settings' && (
        <SettingsTabContent cycle={cycle} />
      )}

      {/* Audit Pack Modal */}
      {auditPackModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="audit-pack-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setAuditPackModal({ open: false, data: null })} />
          <div className="relative bg-[#111111] border border-[#1F1F1F] rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-white">Audit Pack Generated</h2>
                <p className="text-sm text-[#6B6B6B] mt-1">{auditPackModal.data?.pack_id}</p>
              </div>
              <button 
                onClick={() => setAuditPackModal({ open: false, data: null })}
                className="text-[#6B6B6B] hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            {/* Pack Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-[#0A0A0A] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-emerald-400">{auditPackModal.data?.summary?.total_evidence || 0}</div>
                <div className="text-xs text-[#6B6B6B]">Evidence Items</div>
                <div className="text-[10px] text-emerald-400 mt-1">{auditPackModal.data?.summary?.approved_evidence || 0} approved</div>
              </div>
              <div className="bg-[#0A0A0A] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-400">{auditPackModal.data?.summary?.total_claims || 0}</div>
                <div className="text-xs text-[#6B6B6B]">Claims</div>
                <div className="text-[10px] text-blue-400 mt-1">{auditPackModal.data?.summary?.approved_claims || 0} approved</div>
              </div>
              <div className="bg-[#0A0A0A] rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-400">{auditPackModal.data?.summary?.total_audit_events || 0}</div>
                <div className="text-xs text-[#6B6B6B]">Audit Events</div>
              </div>
            </div>

            {/* Verification Status */}
            <div className="bg-[#0A0A0A] rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white">Verification</span>
                <span className={`px-2 py-1 text-xs rounded ${
                  auditPackModal.data?.verification?.chain_verified 
                    ? 'bg-emerald-500/20 text-emerald-400' 
                    : 'bg-amber-500/20 text-amber-400'
                }`}>
                  {auditPackModal.data?.verification?.chain_verified ? 'Chain Verified' : 'Pending Verification'}
                </span>
              </div>
              <div className="text-xs text-[#6B6B6B] font-mono break-all">
                Hash: {auditPackModal.data?.verification?.pack_hash?.substring(0, 32)}...
              </div>
            </div>

            {/* Cycle Info */}
            <div className="bg-[#0A0A0A] rounded-lg p-4 mb-6">
              <h3 className="text-sm font-medium text-white mb-3">Disclosure Cycle</h3>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-[#6B6B6B]">Name</span>
                  <span className="text-white">{auditPackModal.data?.disclosure_cycle?.cycle_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6B6B6B]">Framework</span>
                  <span className="text-white">{auditPackModal.data?.disclosure_cycle?.framework_code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6B6B6B]">Status</span>
                  <span className="text-white capitalize">{auditPackModal.data?.disclosure_cycle?.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6B6B6B]">Progress</span>
                  <span className="text-white">{auditPackModal.data?.disclosure_cycle?.overall_progress}%</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button 
                onClick={downloadAuditPack}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg transition-colors"
                data-testid="download-audit-pack-btn"
              >
                <Download size={16} />
                Download JSON
              </button>
              <button 
                onClick={() => setAuditPackModal({ open: false, data: null })}
                className="flex-1 px-4 py-3 bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] rounded-lg transition-colors"
                data-testid="close-modal-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
